import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RotateLeftIcon from 'mdi-react/RotateLeftIcon';
import RotateRightIcon from 'mdi-react/RotateRightIcon';
import ArrowExpandIcon from 'mdi-react/ArrowExpandIcon';
import ArrowCollapseIcon from 'mdi-react/ArrowCollapseIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import CalendarRemoveOutlineIcon from 'mdi-react/CalendarRemoveOutlineIcon';
import AccountGroupIcon from 'mdi-react/AccountGroupIcon';
import MagnifyPlusOutlineIcon from 'mdi-react/MagnifyPlusOutlineIcon';
import MagnifyMinusOutlineIcon from 'mdi-react/MagnifyMinusOutlineIcon';
import StyleConstants from '../../../core/styles.constants';
import {
  MAX_DESK_ICON_SCALE,
  MIN_DESK_ICON_SCALE,
  isAsDrawn,
  type DeskFeature,
  type DeskShapeFeature,
  type EditorFeature,
  type IconLabelFeature,
  type PolygonFeature,
  type TextLabelFeature,
} from '../models/editor-feature';
import type { FloorPlanRoomOption } from '../models/floorplan-room';
import type { EditorDocument } from '../hooks/use-editor-document';
import type { Scale } from '../utils/measurement';
import type { Selection } from '../hooks/use-selection';
import { isKnownMdiIcon } from '../utils/mdi-icons';
import { selectionKeyOf } from '../utils/selection';
import { containsPoint } from '../utils/hit-test';
import { bookableDeskCountOf, canDeleteSelection } from '../utils/deletion-guard';
import type { WorkplaceRetirementFailure } from '../services/floorplan-editor.service';
import DeskIconField from './desk-icon-field.component';
import {
  describePod, pairShapesToDesks, podDesksOf, podIdOfSelection,
} from '../utils/pod-layout';
import DeskGroupLayoutField from './desk-group-layout-field.component';
import DeskSizeField from './desk-size-field.component';
import DetailSlider from './detail-slider.component';
import SplitPartsNotice from './split-parts-notice.component';
import RoomSelectField from './room-select-field.component';
import WorkplaceReservationsDialog from './workplace-reservations-dialog.component';
import ConfirmDialog from './confirm-dialog.component';
import RetireDeskDialog from './retire-desk-dialog.component';
import WorkplaceQrCode from './workplace-qr-code.component';

interface Props {
  readonly selection: Selection;
  readonly document: EditorDocument;
  /** Units per metre, or null on a plan nobody has measured. Every desk size is in metres. */
  readonly scale: Scale;
  readonly bookableFeatureIds: ReadonlySet<string>;
  readonly rooms: FloorPlanRoomOption[];
  /** The storey being edited, so its own rooms are offered first. */
  readonly storey: number;
  /** Rooms already used by desks on this storey, including ones not yet saved. */
  readonly roomIdsOnThisFloor: ReadonlySet<string>;
  /** The rooms could not be read, so the picker must not claim the office has none. */
  readonly roomsUnavailable: boolean;
  /**
   * Deletes the selection. Owned by the editor rather than done here, because deleting a
   * bookable desk reduces its room's capacity and has to be confirmed first.
   */
  readonly onDelete: () => void;
  /**
   * When each desk goes out of service, for the desks that have a date. A miss means the desk
   * is not retiring, which is every desk until an administrator schedules one.
   */
  readonly retirementDates: ReadonlyMap<string, string>;
  /**
   * Desks whose retirement has already been carried out.
   *
   * These are past the point of being cancelled or rescheduled: the capacity, the bookings and the
   * QR link have all moved, and the server refuses both actions with 488. The editor stops
   * offering them rather than offering them and reporting a failure.
   */
  readonly appliedRetirementIds: ReadonlySet<string>;
  /** Earliest date a retirement may be scheduled for, resolved server-side. */
  readonly earliestRetirementDate: string;
  readonly planPeriodDays: number;
  readonly retirementSaving: boolean;
  readonly retirementError?: WorkplaceRetirementFailure;
  /** Schedules the desk's withdrawal. Owned by the editor, which refreshes the plan after. */
  /** Resolves to whether the server accepted it, which is what closes the dialog. */
  readonly onScheduleRetirement: (
    workplaceId: string,
    retiresOn: string,
    allowInsideBookingWindow: boolean,
  ) => Promise<boolean>;
  /** Clears a scheduled withdrawal, putting the desk back in service. */
  readonly onCancelRetirement: (workplaceId: string) => void;
}

const ROTATION_STEP = 15;

/** A tenth per press: small enough to tune with, large enough to be worth a click. */
const SCALE_STEP = 1.1;

const countOf = (features: EditorFeature[], kind: EditorFeature['kind']): number => features
  .filter((feature) => feature.kind === kind).length;

/**
 * The properties of whatever is selected.
 *
 * Driven by the selection rather than laid out as permanently visible toolbars: with four
 * element types and no more than one or two selected at a time, most of those controls
 * would be inert most of the time, and an inert rotation field next to a selected text
 * label is an invitation to wonder which thing it rotates.
 */
const EditorInspector = ({
  selection,
  document: editorDocument,
  scale,
  bookableFeatureIds,
  rooms,
  storey,
  roomIdsOnThisFloor,
  roomsUnavailable,
  onDelete,
  retirementDates,
  appliedRetirementIds,
  earliestRetirementDate,
  planPeriodDays,
  retirementSaving,
  retirementError,
  onScheduleRetirement,
  onCancelRetirement,
}: Props): React.JSX.Element => {
  const { t, i18n } = useTranslation();
  const { features, keys } = selection;
  const [reservationsOpen, setReservationsOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [reinstateOpen, setReinstateOpen] = useState(false);

  // Which desk the open dialog belongs to. Without this the dialog survives a change of
  // selection — clear the selection and click another desk, and it springs back open showing
  // the new desk, which nobody asked for.
  const selectedDeskId = features.length === 1 && features[0].kind === 'desk' ? features[0].featureId : undefined;

  useEffect(() => {
    setReservationsOpen(false);
    setRetireOpen(false);
  }, [selectedDeskId]);

  /**
   * The group this selection is, when it is exactly one whole group.
   *
   * Selecting any desk of a pod expands to the whole pod, so this is what an admin sees after a
   * single click on a stamped bench — and the moment they are most likely to want it spaced
   * differently, having now seen it in the room.
   */
  const podId = podIdOfSelection(features);
  const podDesks = podId && editorDocument.floorPlan
    ? podDesksOf(editorDocument.floorPlan, podId)
    : [];
  // Reading the arrangement walks every desk of the group against every outline on the plan, and
  // this panel re-renders on things that have nothing to do with the drawing — a dialog opening,
  // a retirement saving. Held until the drawing or the group actually changes.
  const podLayout = useMemo(
    () => (podId && editorDocument.floorPlan
      ? describePod(editorDocument.floorPlan, podDesksOf(editorDocument.floorPlan, podId), scale)
      : undefined),
    [podId, editorDocument.floorPlan, scale],
  );

  /**
   * The drawn outlines under the selected desks, which are what a desk's size is.
   *
   * A desk marker is a point and has no size; the furniture outline beside it does. Paired the
   * same way the delete and the group layout pair them, so the outline sized here is the one
   * that moves and goes with the desk everywhere else.
   */
  const selectedDesks = features.filter((feature): feature is DeskFeature => feature.kind === 'desk');
  const deskOutlines = useMemo(() => {
    if (!editorDocument.floorPlan || !selectedDesks.length) {
      return [];
    }

    const paired = pairShapesToDesks(editorDocument.floorPlan, selectedDesks, scale);
    return selectedDesks.flatMap((desk) => {
      const outline = paired.get(desk.id);

      // Only the outline actually under the desk. Pairing goes by nearness, and a meeting table
      // or a cabinet beside a desk is near it too — resizing that from the desk's panel would
      // change a piece of furniture nobody selected. Tested by containment rather than by size,
      // so a desk made bigger here is still found the next time it is clicked.
      return outline && containsPoint(outline.coordinates, desk.coordinates) ? [outline] : [];
    });
    // The desks are derived from `features`, which is what changes when the selection does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorDocument.floorPlan, features, scale]);

  if (!features.length) {
    return (
      <Box sx={{ p: 2, color: StyleConstants.GREY_COLOR }}>
        <Typography variant="body2" color={StyleConstants.GREY_COLOR}>
          {t('floorplan-editor-nothing-selected')}
        </Typography>
      </Box>
    );
  }

  const kinds = new Set(features.map((feature) => feature.kind));
  const single = features.length === 1 ? features[0] : undefined;

  /**
   * For a desk drawing that is not a desk: make it an ordinary floor shape.
   *
   * The server marks desk drawings by what sits next to a desk, and a cabinet or a stub of wall
   * beside one gets marked too. The type is what stops it being marked as a desk again on the
   * next load; furniture, a wall and a fitting are what it turns out to be.
   */
  const notADeskControls = (): React.JSX.Element => (
    <Stack spacing={0.5}>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t('floorplan-editor-not-a-desk')}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {(['furniture', 'wall', 'fixture'] as const).map((elementType) => (
          <Button
            key={elementType}
            size="small"
            variant="outlined"
            onClick={(): void => selection.selectKeys(editorDocument.markNotDesk(keys, elementType))}
            sx={{ textTransform: 'none' }}
          >
            {t(`floorplan-editor-not-a-desk-${elementType}`)}
          </Button>
        ))}
      </Stack>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t('floorplan-editor-not-a-desk-hint')}
      </Typography>
    </Stack>
  );

  /**
   * The width and height of the outline drawn under a desk, when it has one.
   *
   * Nothing is offered for a desk without one. A bookable desk is a seat, and drawing furniture
   * under it is not something to do from the seat's own panel; its icon size, below, is what
   * makes it easier to see.
   */
  const deskSizeControls = (): React.JSX.Element | null => (deskOutlines.length
    ? <DeskSizeField shapes={deskOutlines} scale={scale} onRedraw={editorDocument.redrawShapes} />
    : null);

  /**
   * How big the desk icon is drawn: smaller, bigger, or back to the standard size.
   *
   * A percentage of the standard, because that is the only size a desk icon has — it is a
   * marker, drawn at the same size on every plan, not a piece of furniture with metres to it.
   */
  const deskIconSizeControls = (desks: readonly DeskFeature[]): React.JSX.Element => {
    const scales = desks.map((desk) => desk.iconScale ?? 1);
    const shared = scales.every((value) => value === scales[0]) ? scales[0] : undefined;
    const atMinimum = scales.every((value) => value <= MIN_DESK_ICON_SCALE);
    const atMaximum = scales.every((value) => value >= MAX_DESK_ICON_SCALE);
    const deskKeys = new Set(desks.map(selectionKeyOf));

    return (
      <Stack spacing={0.5}>
        <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
          {t('floorplan-editor-desk-icon-size')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={String(t('floorplan-editor-shrink'))}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={atMinimum}
                onClick={(): void => editorDocument.scaleBy(deskKeys, 1 / SCALE_STEP)}
                aria-label={String(t('floorplan-editor-desk-icon-smaller'))}
              >
                <MagnifyMinusOutlineIcon size={16} />
              </Button>
            </span>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{ minWidth: 48, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
            aria-live="polite"
          >
            {shared === undefined ? t('floorplan-editor-desk-size-mixed') : `${Math.round(shared * 100)}%`}
          </Typography>
          <Tooltip title={String(t('floorplan-editor-enlarge'))}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={atMaximum}
                onClick={(): void => editorDocument.scaleBy(deskKeys, SCALE_STEP)}
                aria-label={String(t('floorplan-editor-desk-icon-bigger'))}
              >
                <MagnifyPlusOutlineIcon size={16} />
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            disabled={shared === 1}
            onClick={(): void => editorDocument.updateProperties<DeskFeature>(deskKeys, 'desk', { iconScale: 1 })}
            sx={{ textTransform: 'none' }}
          >
            {t('floorplan-editor-desk-icon-size-reset')}
          </Button>
        </Stack>
      </Stack>
    );
  };

  // Offered for anything with a size. A desk marker has none — every client draws it at its
  // own fixed size — so the control is hidden rather than shown doing nothing.
  const resizable = features.some((feature) => feature.kind !== 'desk');

  /**
   * Whether this desk has anything to look up.
   *
   * A desk is only bookable because a `Workplaces` row exists for its featureId, so one the
   * editor has just placed has no reservations and no row to ask about. When the rooms could
   * not be read at all, the assignments are empty for a reason that says nothing about this
   * desk — so the button stays enabled and the server decides, rather than the editor
   * claiming a desk is unbookable on data it never got.
   */
  const canShowReservations = single?.kind === 'desk'
    && (roomsUnavailable || bookableFeatureIds.has(single.featureId));

  /**
   * Whether the selection may be deleted.
   *
   * One bookable desk at a time. Forty desks vanish from a rubber-band drag as easily as one,
   * and each takes a seat off its room and orphans the reservations hanging off its workplace.
   * The button stays visible and says why, since a control that disappears reads as a bug.
   */
  const deletable = canDeleteSelection(features, bookableFeatureIds);
  const bookableDesks = bookableDeskCountOf(features, bookableFeatureIds);

  /**
   * Whether deleting this selection means retiring a desk rather than erasing a drawing.
   *
   * A desk with a `Workplaces` row behind it cannot be deleted in any meaningful sense from
   * here: the row is what makes it bookable, and erasing the dot leaves the row untouched — the
   * desk stays bookable and its QR sticker keeps scanning. So for those the button opens the
   * retirement dialog instead. Freshly drawn desks, labels and shapes still delete outright,
   * because for them the drawing is all there is.
   */
  const retiringInsteadOfDeleting = bookableDesks === 1;

  /** The date this desk goes out of service, if one is already scheduled. */
  const scheduledRetirement = single?.kind === 'desk'
    ? retirementDates.get(single.featureId)
    : undefined;

  /**
   * Whether this desk is already out of service rather than on its way out.
   *
   * The line between "can still be changed" and "has happened" — and the server draws it in the
   * same place, refusing both a reschedule and a cancellation with 488 once the job has run.
   */
  const alreadyOutOfService = single?.kind === 'desk'
    && appliedRetirementIds.has(single.featureId);

  /**
   * Why the remove button is in the state it is in.
   *
   * "Already out of service" comes first: it is the one case where the button is disabled because
   * the work is *done* rather than because something is wrong with the selection.
   */
  const deleteTooltipKey = ((): string => {
    if (alreadyOutOfService) return 'floorplan-editor-retired-already';

    return deletable
      ? 'floorplan-editor-delete-selection'
      : 'floorplan-editor-delete-bulk-refused';
  })();

  const formatRetirementDate = (isoDate: string): string => new Date(isoDate)
    .toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' });

  const sizeControls = (): React.JSX.Element => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" sx={{ minWidth: 40 }}>{t('floorplan-editor-size')}</Typography>
      <Tooltip title={String(t('floorplan-editor-shrink'))}>
        <Button
          size="small"
          variant="outlined"
          onClick={(): void => editorDocument.scaleBy(keys, 1 / SCALE_STEP)}
          aria-label={String(t('floorplan-editor-shrink'))}
        >
          <ArrowCollapseIcon size={16} />
        </Button>
      </Tooltip>
      <Tooltip title={String(t('floorplan-editor-enlarge'))}>
        <Button
          size="small"
          variant="outlined"
          onClick={(): void => editorDocument.scaleBy(keys, SCALE_STEP)}
          aria-label={String(t('floorplan-editor-enlarge'))}
        >
          <ArrowExpandIcon size={16} />
        </Button>
      </Tooltip>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t('floorplan-editor-size-hint')}
      </Typography>
    </Stack>
  );

  const rotationControls = (currentRotation: number, kind: EditorFeature['kind']): React.JSX.Element => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title={String(t('floorplan-editor-rotate-left'))}>
        <Button
          size="small"
          variant="outlined"
          onClick={(): void => editorDocument.rotateBy(keys, -ROTATION_STEP)}
          aria-label={String(t('floorplan-editor-rotate-left'))}
        >
          <RotateLeftIcon size={16} />
        </Button>
      </Tooltip>
      <TextField
        size="small"
        type="number"
        label={String(t('floorplan-editor-rotation'))}
        value={Math.round(currentRotation)}
        onChange={(event): void => editorDocument.rotateTo(keys, Number(event.target.value))}
        sx={{ width: 100 }}
        inputProps={{ 'aria-label': String(t('floorplan-editor-rotation')) }}
      />
      <Tooltip title={String(t('floorplan-editor-rotate-right'))}>
        <Button
          size="small"
          variant="outlined"
          onClick={(): void => editorDocument.rotateBy(keys, ROTATION_STEP)}
          aria-label={String(t('floorplan-editor-rotate-right'))}
        >
          <RotateRightIcon size={16} />
        </Button>
      </Tooltip>
      {kind === 'deskShape' && (
        <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
          {t('floorplan-editor-rotation-moves-shape')}
        </Typography>
      )}
    </Stack>
  );

  /**
   * Which room the selected desks belong to.
   *
   * Read-only for a desk that is already bookable. Its room is not a drawing property: the
   * `Workplaces` row behind it carries that desk's check-ins and reservations, and moving the
   * row to another room silently moves all of it, changing what two rooms report as their
   * occupancy and leaving the people who booked the seat somewhere they never chose. A desk
   * belonging to the wrong room is fixed by deleting it and drawing it in the right one, which
   * asks about the capacity it costs first.
   *
   * Still a choice up to the moment of saving, because until then there is no row and nothing
   * to move — only the drawing, which is exactly what an admin correcting a mistake wants.
   * Locked for a mixed selection as soon as one member is bookable, and locked whenever the
   * rooms could not be read: an empty assignment set means the bookable ones cannot be told
   * apart, and guessing wrong here is the expensive direction.
   */
  const roomControls = (desks: DeskFeature[]): React.JSX.Element => {
    const distinct = new Set(desks.map((desk) => desk.roomId ?? ''));
    const value = distinct.size === 1 ? (desks[0].roomId ?? '') : '';
    const known = rooms.some((room) => room.id === value);
    const locked = roomsUnavailable || desks.some((desk) => bookableFeatureIds.has(desk.featureId));

    if (locked) {
      const roomName = rooms.find((room) => room.id === value)?.name;

      return (
        <Stack spacing={0.5}>
          <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
            {t('floorplan-editor-room')}
          </Typography>
          <Typography variant="body2">
            {/* Three different unknowns, none of which may pose as a room name: desks sitting
                in different rooms, a room this office no longer has, and rooms that could not
                be read at all. */}
            {distinct.size > 1
              ? t('floorplan-editor-room-mixed')
              : roomName ?? t('floorplan-editor-room-unknown')}
          </Typography>
          <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
            {t(roomsUnavailable
              ? 'floorplan-editor-rooms-unavailable-short'
              : 'floorplan-editor-room-cannot-move')}
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={0.5}>
        <RoomSelectField
          rooms={rooms}
          storey={storey}
          value={known ? value : ''}
          onChange={(roomId): void => editorDocument.updateProperties<DeskFeature>(keys, 'desk', { roomId })}
          roomIdsOnThisFloor={roomIdsOnThisFloor}
          helperText={String(t('floorplan-editor-room-help'))}
          unavailable={roomsUnavailable}
        />
        {/* Said before the save rather than after it, because afterwards it is no longer
            advice — it is an explanation of a control that has gone. */}
        <Typography variant="caption" color={StyleConstants.ORANGE_COLOR}>
          {t('floorplan-editor-room-change-before-save')}
        </Typography>
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 2, overflowY: 'auto', color: StyleConstants.GREY_COLOR }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {Array.from(kinds).map((kind) => (
          <Chip key={kind} size="small" label={`${countOf(features, kind)} × ${t(`floorplan-editor-kind-${kind}`)}`} />
        ))}
      </Stack>

      {/* A group is re-arranged rather than deleted and stamped again, so its layout stops
          being final the moment it is placed. Shown from any one of its desks, because
          clicking a desk selects that desk — the group is not a unit of selection, only of
          arrangement. Hidden rather than disabled when the group cannot be read: an unmeasured
          plan has no metres for the fields to be in, desks drawn with no outline have no size
          to space apart, and a group somebody has pulled out of line is in no arrangement to
          name. */}
      {podId && podLayout && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <DeskGroupLayoutField
            layout={podLayout}
            seats={podDesks.length}
            onChange={(next): void => editorDocument.relayoutDeskGroup(
              podId,
              next,
              scale,
              `pod-layout-${podId}`,
            )}
          />
          <Divider />
        </Stack>
      )}

      {single?.kind === 'desk' && (
        <Stack spacing={2}>
          {/* Silent when the rooms could not be read: nothing is known about this desk's
              workplace then, and "not bookable" is a claim, not an absence of one. */}
          {!roomsUnavailable && !bookableFeatureIds.has(single.featureId) && (
            <Chip
              size="small"
              // A room already chosen means the workplace appears the moment this is saved,
              // which is a different situation from a desk nobody can book at all.
              color={single.roomId ? 'info' : 'warning'}
              label={String(t(single.roomId
                ? 'floorplan-editor-desk-bookable-after-save'
                : 'floorplan-editor-desk-not-bookable'))}
              title={String(t('floorplan-editor-desk-not-bookable-explanation'))}
            />
          )}
          {/* A desk on its way out. Stated as a date rather than a plain "retiring" flag,
              because the date is the whole point: everything booked before it still happens. */}
          {!!scheduledRetirement && (
            <Chip
              size="small"
              color={alreadyOutOfService ? 'default' : 'warning'}
              label={String(t(
                alreadyOutOfService
                  ? 'floorplan-editor-retired-badge'
                  : 'floorplan-editor-retire-badge',
                { date: formatRetirementDate(scheduledRetirement) },
              ))}
              title={String(t(
                alreadyOutOfService
                  ? 'floorplan-editor-retired-badge-help'
                  : 'floorplan-editor-retire-badge-help',
                { date: formatRetirementDate(scheduledRetirement) },
              ))}
            />
          )}
          {roomControls([single])}
          <DeskIconField
            iconName={single.iconName}
            onChange={(iconName): void => editorDocument.updateProperties<DeskFeature>(
              keys,
              'desk',
              { iconName },
            )}
          />
          {deskIconSizeControls([single])}
          {deskSizeControls()}
          {rotationControls(single.rotation, 'desk')}
        </Stack>
      )}

      {single?.kind === 'deskShape' && (
        <Stack spacing={2}>
          <DeskIconField
            kind="furniture"
            iconName={(single as DeskShapeFeature).iconName}
            onChange={(iconName): void => editorDocument.updateProperties<DeskShapeFeature>(
              keys,
              'deskShape',
              { iconName },
            )}
          />
          {/* Still offered with an icon chosen: the outline is the footprint, it is what the
              desk detector reads, and it is what a client that knows nothing about icons
              draws. Sizing it is not made pointless by drawing something else on top. */}
          <SplitPartsNotice
            coordinates={(single as DeskShapeFeature).coordinates}
            onSplit={(): void => selection.selectKeys(editorDocument.splitParts(single.id))}
          />
          <DeskSizeField
            shapes={[single as DeskShapeFeature]}
            scale={scale}
            onRedraw={editorDocument.redrawShapes}
          />
          {rotationControls((single as DeskShapeFeature).rotation, 'deskShape')}
          {sizeControls()}
          {notADeskControls()}
        </Stack>
      )}

      {single?.kind === 'polygon' && (
        <Stack spacing={2}>
          <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
            {t(`floorplan-editor-layer-${single.layer === 'room' ? 'rooms' : single.layer}`)}
          </Typography>

          {/* A zone is a bookable room because its identifier *is* the room's. Drawn
              freehand, or accepted from a suggestion, it carries a fresh identifier and is
              therefore a coloured area and nothing more — which looks finished and books
              nobody. This is where that is fixed. */}
          {single.layer === 'room' && !roomsUnavailable && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-zone-room')}
              </Typography>
              <RoomSelectField
                value={rooms.some((room) => room.id === single.featureId) ? single.featureId : ''}
                rooms={rooms}
                storey={storey}
                roomIdsOnThisFloor={roomIdsOnThisFloor}
                onChange={(roomId): void => editorDocument.updateProperties<PolygonFeature>(
                  keys,
                  'polygon',
                  { featureId: roomId },
                )}
              />
              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {rooms.some((room) => room.id === single.featureId)
                  ? t('floorplan-editor-zone-room-hint')
                  : t('floorplan-editor-zone-room-unlinked')}
              </Typography>
            </Stack>
          )}

          {/* Only for a wall the editor drew: the thickness is meaningful because the
              centreline it was swept from is still there. A structural element imported from
              CAD is an outline with no line behind it, and there is nothing to re-sweep. */}
          {single.centreline !== null && single.thickness !== null && (
            <TextField
              size="small"
              type="number"
              label={String(t('floorplan-editor-thickness'))}
              value={single.thickness}
              onChange={(event): void => {
                const metres = Number(event.target.value);

                if (Number.isFinite(metres) && metres > 0) {
                  editorDocument.setThickness(single.id, metres, scale);
                }
              }}
              inputProps={{ min: 0.01, step: 0.05 }}
            />
          )}

          {/* Kept as drawn from a traced picture: what nobody marked. Saying what it is styles it
              as one, and keeps the desk tagger off it as the drawing's type does. */}
          {isAsDrawn(single) && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-as-drawn')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {(['furniture', 'wall', 'fixture'] as const).map((elementType) => (
                  <Button
                    key={elementType}
                    size="small"
                    variant="outlined"
                    onClick={(): void => editorDocument.updateProperties<PolygonFeature>(keys, 'polygon', { elementType })}
                    sx={{ textTransform: 'none' }}
                  >
                    {t(`floorplan-editor-not-a-desk-${elementType}`)}
                  </Button>
                ))}
              </Stack>
            </Stack>
          )}

          <SplitPartsNotice
            coordinates={single.coordinates}
            onSplit={(): void => selection.selectKeys(editorDocument.splitParts(single.id))}
          />

          {/* Only for an outline with detail to give up, which in practice means one traced
              from CAD. Placed with the shape's own properties rather than on the tool, because
              it is a fact about this outline rather than about what the pointer is doing. */}
          <DetailSlider
            featureId={single.id}
            coordinates={single.coordinates}
            onChange={(coordinates): void => editorDocument.setDetail(single.id, coordinates)}
          />

          {rotationControls(single.rotation, 'polygon')}
          {sizeControls()}
        </Stack>
      )}

      {single?.kind === 'textLabel' && (
        <Stack spacing={2}>
          <TextField
            size="small"
            label={String(t('floorplan-editor-text'))}
            value={single.text}
            onChange={(event): void => editorDocument.updateProperties<TextLabelFeature>(keys, 'textLabel', {
              text: event.target.value,
            })}
          />
          <TextField
            size="small"
            type="number"
            label={String(t('floorplan-editor-font-size'))}
            value={single.fontSize}
            onChange={(event): void => editorDocument.updateProperties<TextLabelFeature>(keys, 'textLabel', {
              fontSize: Math.max(1, Number(event.target.value)),
            })}
          />
          <TextField
            size="small"
            type="color"
            label={String(t('floorplan-editor-colour'))}
            value={single.color}
            onChange={(event): void => editorDocument.updateProperties<TextLabelFeature>(keys, 'textLabel', {
              color: event.target.value,
            })}
          />
          {rotationControls(single.rotation, 'textLabel')}
          {sizeControls()}
        </Stack>
      )}

      {single?.kind === 'iconLabel' && (
        <Stack spacing={2}>
          <TextField
            size="small"
            label={String(t('floorplan-editor-icon-name'))}
            value={single.iconName ?? ''}
            onChange={(event): void => editorDocument.updateProperties<IconLabelFeature>(keys, 'iconLabel', {
              iconName: event.target.value.trim(),
            })}
            helperText={
              isKnownMdiIcon(single.iconName)
                ? t('floorplan-editor-icon-name-help')
                : t('floorplan-editor-icon-unknown')
            }
            error={!isKnownMdiIcon(single.iconName)}
          />
          <TextField
            size="small"
            type="number"
            label={String(t('floorplan-editor-icon-size'))}
            value={single.iconSize}
            onChange={(event): void => editorDocument.updateProperties<IconLabelFeature>(keys, 'iconLabel', {
              iconSize: Math.max(1, Number(event.target.value)),
            })}
          />
          <TextField
            size="small"
            type="color"
            label={String(t('floorplan-editor-colour'))}
            value={single.color}
            onChange={(event): void => editorDocument.updateProperties<IconLabelFeature>(keys, 'iconLabel', {
              color: event.target.value,
            })}
          />
          {rotationControls(single.rotation, 'iconLabel')}
          {sizeControls()}
        </Stack>
      )}

      {!single && (
        <Stack spacing={2}>
          {/* A mixed selection only offers what every kind understands. Rotation is the one
              property all four have, even though it means something different to a shape. */}
          {kinds.size === 1 && kinds.has('desk') && roomControls(features as DeskFeature[])}
          {/* One icon for the whole selection: tagging forty phone booths one at a time is
              exactly the job this panel exists to avoid. */}
          {kinds.size === 1 && kinds.has('deskShape') && notADeskControls()}
          {kinds.size === 1 && kinds.has('deskShape') && (
            <DeskIconField
              kind="furniture"
              iconName={(features[0] as DeskShapeFeature).iconName}
              onChange={(iconName): void => editorDocument.updateProperties<DeskShapeFeature>(
                keys,
                'deskShape',
                { iconName },
              )}
            />
          )}
          {kinds.size === 1 && kinds.has('desk') && deskIconSizeControls(features as DeskFeature[])}
          {kinds.size === 1 && kinds.has('desk') && deskSizeControls()}
          {kinds.size === 1 && kinds.has('desk') && (
            <DeskIconField
              iconName={(features[0] as DeskFeature).iconName}
              onChange={(iconName): void => editorDocument.updateProperties<DeskFeature>(
                keys,
                'desk',
                { iconName },
              )}
            />
          )}
          {rotationControls(features[0].rotation, features[0].kind)}
          {resizable && sizeControls()}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      {/* A plain flex row rather than a Stack with `spacing`. Stack's spacing is a margin on
          every child but the first, and under `flexWrap` the first child of each wrapped line
          keeps that margin while the line above does not — so two or three buttons that wrap sit
          visibly out of line with each other. `gap` spaces wrapped rows evenly; MUI 5.13 has no
          `useFlexGap` on Stack yet, so the row is expressed directly.

          Each button is wrapped in a span so its Tooltip still works while disabled (a disabled
          button fires no events). A bare span is inline, which puts the buttons on text baselines
          of differing height; inline-flex makes each wrapper exactly its button. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          '& > span': { display: 'inline-flex' },
        }}
      >
        {/* Offered for one desk at a time: the answer is a table of people for a single seat,
            and a multiple selection has no single answer to show. A desk with no Workplaces
            row behind it has nothing to look up at all — the control stays visible and
            explains itself rather than disappearing, since "why can't I see this desk's
            bookings" is the question it exists to answer. */}
        {single?.kind === 'desk' && (
          <Tooltip
            title={String(t(canShowReservations
              ? 'floorplan-editor-reservations-open'
              : 'floorplan-editor-reservations-unavailable'))}
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AccountGroupIcon size={16} />}
                disabled={!canShowReservations}
                onClick={(): void => setReservationsOpen(true)}
              >
                {t('floorplan-editor-reservations-button')}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Offered only for a desk that is already on its way out, and it is the one action
            here that takes effect immediately rather than on save — the retirement lives on the
            desk record, not in the drawing. */}
        {/* Offered for both states, because an administrator has to be able to correct a
            withdrawal — but they are different actions and are labelled differently. Calling off a
            *scheduled* retirement changes nothing that had happened yet. Putting an *applied* one
            back gives the room its seat and makes the desk bookable from today, and does not
            restore the bookings that were moved or re-link the sticker; the dialog says so before
            anything happens. */}
        {!!scheduledRetirement && single?.kind === 'desk' && (
          <Tooltip
            title={String(t(alreadyOutOfService
              ? 'floorplan-editor-retire-reinstate-help'
              : 'floorplan-editor-retire-cancel-help'))}
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CalendarRemoveOutlineIcon size={16} />}
                disabled={retirementSaving}
                onClick={(): void => {
                  if (alreadyOutOfService) {
                    setReinstateOpen(true);
                    return;
                  }

                  onCancelRetirement(single.featureId);
                }}
              >
                {t(alreadyOutOfService
                  ? 'floorplan-editor-retire-reinstate-action'
                  : 'floorplan-editor-retire-cancel-action')}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Nothing left to remove: this desk is already out of service. Offering the button and
            having the server answer 488 tells the administrator only that something went wrong,
            which is not what happened — the action is simply already done. */}
        <Tooltip title={String(t(deleteTooltipKey, { count: bookableDesks }))}>
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<TrashCanOutlineIcon size={16} />}
              disabled={!deletable || alreadyOutOfService}
              onClick={(): void => {
                // A bookable desk is retired, not deleted — erasing its dot would leave it
                // bookable and its sticker scanning, which is the whole defect this replaces.
                if (retiringInsteadOfDeleting && single?.kind === 'desk') {
                  setRetireOpen(true);
                  return;
                }

                onDelete();
              }}
            >
              {t(retiringInsteadOfDeleting ? 'floorplan-editor-retire-button' : 'delete')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Below the actions, because it is something to look at rather than something to do.
          Renders nothing when the desk has no sticker linked, which is most desks. */}
      <WorkplaceQrCode workplaceId={selectedDeskId} />

      <WorkplaceReservationsDialog
        open={reservationsOpen}
        workplaceId={single?.kind === 'desk' ? single.featureId : undefined}
        onClose={(): void => setReservationsOpen(false)}
      />

      {/* Reinstating is not an undo, so it is confirmed rather than done on a single click. What
          it cannot put back is the part worth reading before pressing it. */}
      <ConfirmDialog
        open={reinstateOpen}
        messageKey="floorplan-editor-retire-reinstate-confirm"
        onConfirm={(): void => {
          setReinstateOpen(false);
          if (single?.kind === 'desk') onCancelRetirement(single.featureId);
        }}
        onCancel={(): void => setReinstateOpen(false)}
      />

      <RetireDeskDialog
        open={retireOpen}
        workplaceId={single?.kind === 'desk' ? single.featureId : undefined}
        earliestRetirementDate={earliestRetirementDate}
        planPeriodDays={planPeriodDays}
        saving={retirementSaving}
        error={retirementError}
        onConfirm={(retiresOn, allowInsideBookingWindow): void => {
          if (single?.kind !== 'desk') return;

          // Closed only once the server has accepted it. Closing on click would hide the refusal
          // the dialog exists to show - the date inside the booking window, the retirement the
          // job already applied - and leave the administrator with a dialog that vanished and a
          // desk that never went out of service.
          //
          // Wrapped in Promise.resolve rather than chained directly. The handler is typed to
          // return a promise, but a caller that returns nothing is a plain JavaScript value away
          // from crashing the click - which is exactly what a half-applied hot reload produces
          // during development, and what any future caller written against the old void-returning
          // shape would produce in earnest. Undefined simply means "not confirmed", so the dialog
          // stays open, which is the safe direction.
          Promise.resolve(onScheduleRetirement(single.featureId, retiresOn, allowInsideBookingWindow))
            .then((scheduled) => {
              if (scheduled) setRetireOpen(false);
            })
            .catch(() => undefined);
        }}
        onClose={(): void => setRetireOpen(false)}
      />
    </Box>
  );
};

export default EditorInspector;
